import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  TOOL_APPROVAL_FILTERS,
  toolApprovalFilterSchema,
  type ToolApprovalFilter,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';

/** Landing view of every Shadow AI Tools tab (KIR-4854). */
export const DEFAULT_TOOL_APPROVAL_FILTER: ToolApprovalFilter =
  TOOL_APPROVAL_FILTERS.NOT_APPROVED;

/**
 * URL param holding a table's Show filter. Deliberately `<tableId>-appr`, NOT
 * `<tableId>.appr`: the DataTable owns — and on every sync wipes — all
 * `<tableId>.`-prefixed params (see `clearTableParams`), and the Show filter
 * is a bespoke control, not a registered column.
 */
export function getToolApprovalUrlParam(tableId: string): string {
  return `${tableId}-appr`;
}

/** Unknown / missing values fall back to the default (Not approved). */
export function parseToolApprovalFilter(
  raw: string | null | undefined
): ToolApprovalFilter {
  const parsed = toolApprovalFilterSchema.safeParse(raw);
  return parsed.success ? parsed.data : DEFAULT_TOOL_APPROVAL_FILTER;
}

export interface ToolApprovalFilterState {
  filter: ToolApprovalFilter;
  setFilter: (next: ToolApprovalFilter) => void;
}

/**
 * Show-filter selection for one table. With `persistInUrl` it round-trips
 * through `urlParam` (absent = Not approved, so the default view keeps a clean
 * URL); otherwise it is component state — the user-scope drill-down, whose
 * tables run with URL state off.
 */
export function useToolApprovalFilterState({
  urlParam,
  persistInUrl,
}: {
  urlParam: string;
  persistInUrl: boolean;
}): ToolApprovalFilterState {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localFilter, setLocalFilter] = useState<ToolApprovalFilter>(
    DEFAULT_TOOL_APPROVAL_FILTER
  );

  const filter = persistInUrl
    ? parseToolApprovalFilter(searchParams.get(urlParam))
    : localFilter;

  const setFilter = useCallback(
    (next: ToolApprovalFilter): void => {
      if (!persistInUrl) {
        setLocalFilter(next);
        return;
      }
      // Functional update: the DataTable syncs its own params into the same
      // URL, so never overwrite from a possibly stale snapshot.
      setSearchParams(
        prev => {
          const nextParams = new URLSearchParams(prev);
          if (next === DEFAULT_TOOL_APPROVAL_FILTER) {
            nextParams.delete(urlParam);
          } else {
            nextParams.set(urlParam, next);
          }
          return nextParams;
        },
        { replace: true }
      );
    },
    [persistInUrl, setSearchParams, urlParam]
  );

  return { filter, setFilter };
}
